/**
 * El margen de solape de las lecturas incrementales.
 *
 * Las lecturas contra Business Central piden `Source_Counter gt <marca>`, y la
 * marca sale del mayor contador que la caja ya tiene. El trato es que el
 * contador sólo sube: una fila que cambia recibe un número más alto que
 * cualquiera visto antes, así que pedir «lo mayor que mi marca» trae todo lo
 * nuevo y nada repetido.
 *
 * **Ese trato no se cumple hoy.** Editar en Excel hace retroceder el contador
 * (caso H13), y cuando eso pasa la fila queda por debajo de la marca de una
 * caja que ya pasó de largo: no llega tarde, **no llega nunca**, sin error y
 * sin rastro. La mitad de Business Central es sembrar una `NumberSequence` en
 * las tablas; ésta es la mitad de acá, y es un colchón, no una cura: pedir un
 * poco por debajo de la marca para que una renumeración pequeña vuelva sola.
 *
 * ## Por qué el margen se mide en filas y no en unidades de contador
 *
 * Porque el contador no tiene la misma densidad en todas partes, y no por poco.
 * Medido el 21/09/2026 sobre `ListaClientes`, con la misma extensión en los dos
 * ambientes:
 *
 * - **CRI DEV**: 14.454 filas entre los contadores 2 y 14.645. Casi denso: el
 *   contador es prácticamente el número de orden.
 * - **PAN DEV**: 2.193 filas entre 6 y 171.712. Disperso: quema números como
 *   una secuencia compartida.
 *
 * Con un margen de «resta 1.000», Costa Rica releería alrededor de mil filas en
 * cada sincronización y Panamá **ninguna**: el mismo número significa cosas
 * distintas en cada sitio, y el ambiente donde más barato parece es justo donde
 * no protege. Con «la K-ésima marca más alta», el coste es K filas en los dos,
 * hoy y cuando la densidad cambie.
 */

/**
 * Cuántas filas se vuelven a pedir en cada lectura.
 *
 * Cincuenta es barato en todas las entidades que lo usan --la mayor tiene
 * decenas de miles de filas-- y cubre la renumeración de un puñado de fichas,
 * que es la forma en que esto se ha visto. Una renumeración masiva no la cubre
 * ningún margen razonable: eso lo arregla la `NumberSequence` de Business
 * Central, y hasta entonces se detecta comparando conteos, no releyendo.
 */
export const FILAS_DE_SOLAPE = 50;

/**
 * La marca desde la que volver a pedir: la K-ésima más alta, no la más alta.
 *
 * Es inmune a contadores ausentes o no numéricos, que el comparador de un
 * `sort` restando convertiría en `NaN` --y un comparador que devuelve `NaN`
 * deja el orden sin definir, así que «el último elemento» puede no ser el
 * máximo--. Tres servicios lo hacían así.
 *
 * @param {Array<Object|number>} filas Filas locales, o contadores sueltos.
 * @param {number} [filasDeSolape] Cuántas filas de colchón. Por omisión, `FILAS_DE_SOLAPE`.
 * @param {string} [campo] De dónde sacar el contador de cada fila.
 * @returns {number|null} La marca, o `null` si no hay ni un contador válido
 *   --que significa «no hay desde dónde seguir», y el llamante pide todo--.
 */
export const marcaConSolape = (
  filas,
  filasDeSolape = FILAS_DE_SOLAPE,
  campo = "Source_Counter"
) => {
  // `Number(null)` y `Number("")` valen 0, que es finito: sin este filtro, una
  // fila **sin** contador se colaría como una fila **con** contador cero y
  // arrastraría la marca hasta el principio en cada lectura. No es hipotético:
  // las direcciones de cliente guardan `Source_Counter: null` cuando Business
  // Central no lo manda.
  const sinDato = (valor) => valor === null || valor === undefined || valor === "";

  const contadores = (filas || [])
    .map((fila) => (typeof fila === "object" && fila !== null ? fila?.[campo] : fila))
    .filter((valor) => !sinDato(valor))
    .map(Number)
    .filter((valor) => Number.isFinite(valor));

  if (contadores.length === 0) return null;

  // Descendente, y se toma la K-ésima. Con menos de K filas se toma la más
  // baja, que en la práctica es pedirlo todo: es lo correcto, porque una caja
  // con cuatro filas no tiene nada que ahorrar.
  contadores.sort((a, b) => b - a);
  const cuantas = Math.max(1, Math.trunc(filasDeSolape) || 1);

  return contadores[Math.min(cuantas, contadores.length) - 1];
};

/**
 * La marca a guardar cuando el checkpoint no se deriva de las filas locales
 * sino que se persiste --las bajas y las direcciones--.
 *
 * Mismo colchón, pero decidido al escribir: en vez de guardar el mayor
 * contador del lote se guarda el K-ésimo, y así la lectura siguiente vuelve a
 * pasar por esas filas sin que haga falta restarle nada a la marca.
 *
 * Reaplicar lo que ya se aplicó no cuesta: una baja de una fila que ya no está
 * no hace nada, y un maestro se vuelve a escribir igual que estaba.
 *
 * @param {Array<Object|number>} filasDelLote Lo que trajo esta lectura.
 * @param {number} marcaAnterior La marca con la que se entró, que nunca retrocede.
 * @param {number} [filasDeSolape]
 * @param {string} [campo]
 * @returns {number} La marca a persistir.
 */
export const marcaAGuardarConSolape = (
  filasDelLote,
  marcaAnterior,
  filasDeSolape = FILAS_DE_SOLAPE,
  campo = "Source_Counter"
) => {
  const propuesta = marcaConSolape(filasDelLote, filasDeSolape, campo);
  const anterior = Number.isFinite(Number(marcaAnterior)) ? Number(marcaAnterior) : 0;

  // La marca no retrocede por un lote corto: si esta vuelta trajo tres filas,
  // su tercera más alta puede estar por debajo de donde ya íbamos, y bajar ahí
  // haría releer desde muy atrás en cada sincronización.
  if (propuesta === null) return anterior;

  return Math.max(propuesta, anterior);
};
